import React, { useEffect, useMemo } from 'react';
import * as R from 'remeda';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { CssBaseline, ThemeProvider } from '@rossum/ui/material';
import { HTML_FONT_SIZE, resolveTheme, ThemeMode } from '@rossum/ui/theme';
import { defaultTheme } from './constants/values';
import {
  oldThemeTypeToNewTheme,
  organizationBranding,
} from './features/client-config/organization/branding';
import { useIsRunningInCoupa } from './features/coupa/isRunningInCoupa';
import { useIsFeatureEnabled } from './features/feature-enablement/featureEnablement';
import { safeOrganizationSelector } from './redux/modules/organization/selectors';
import { State } from './types/state';

const THEME_KEY = 'rossum:theme';

type RuiThemeProviderProps = {
  children: React.ReactNode;
  theme?: ThemeMode;
  isRoot?: boolean;
};

const parseTheme = (theme: string | null) =>
  z.enum(['dark', 'light', 'coupa']).safeParse(theme);

export const RuiThemeProvider = ({
  children,
  theme,
  isRoot = true,
}: RuiThemeProviderProps) => {
  const organization = useSelector(safeOrganizationSelector);
  const isInCoupa = useIsRunningInCoupa();
  const isDemoMode = useIsFeatureEnabled('demo-mode');

  const cachedTheme = parseTheme(localStorage.getItem(THEME_KEY));
  const cachedThemeOverride = cachedTheme.success
    ? cachedTheme.data
    : undefined;

  const searchParams = new URLSearchParams(window.location.search);
  const parsedSearchParamOverride = parseTheme(searchParams.get('theme'));
  const searchParamThemeOverride = parsedSearchParamOverride.success
    ? parsedSearchParamOverride.data
    : undefined;

  const userTheme = useSelector((state: State) => state.user.uiSettings.theme);

  const uiSettings = useMemo(
    () => (isDemoMode ? {} : (organization?.uiSettings ?? {})),
    [isDemoMode, organization?.uiSettings]
  );

  const orgTheme = organizationBranding.defaultTheme(uiSettings);

  const currentTheme =
    theme ??
    (userTheme ? oldThemeTypeToNewTheme(userTheme) : orgTheme) ??
    searchParamThemeOverride ??
    cachedThemeOverride ??
    defaultTheme;

  const effectiveTheme: ThemeMode = isInCoupa ? 'coupa' : currentTheme;

  useEffect(() => {
    if (isRoot && !isInCoupa) {
      localStorage.setItem(THEME_KEY, effectiveTheme);
    }
  }, [effectiveTheme, isRoot, isInCoupa]);

  const themeConfig = useMemo(() => {
    const fontFamily = organizationBranding.fontFamily(uiSettings);
    const square = organizationBranding.square(uiSettings);
    const primaryColor = organizationBranding.primaryColor(uiSettings);

    const resolvedTheme = resolveTheme(effectiveTheme, {
      // needs to be added to respect font-size: 10px on html
      typography: {
        htmlFontSize: HTML_FONT_SIZE,
        ...(fontFamily
          ? {
              fontFamily,
            }
          : {}),
      },
      // TODO: Document this and add value different than 0
      ...(square
        ? {
            shape: {
              borderRadius: 0,
            },
          }
        : {}),
      components: {
        MuiSelect: {
          styleOverrides: {
            select: {
              height: 0,
              lineHeight: 1.5,
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            // Fix for beamer snippets with white titles on white background.
            '.beamerAnnouncementSnippetContent': {
              backgroundColor: 'white',
            },
            '.beamerAnnouncementSnippetTitle, .beamerAnnouncementSnippetText': {
              color: 'black',
            },
            body: {
              lineHeight: 1.42857143,
              fontSize: '1.4rem',
              letterSpacing: 'unset',
            },
          },
        },
      },
    });

    return {
      ...resolvedTheme,
      // custom branding - need `augmentColor` to generate dark/light shades
      palette: primaryColor
        ? {
            ...resolvedTheme.palette,
            primary: resolvedTheme.palette.augmentColor({
              color: { main: primaryColor },
            }),
          }
        : resolvedTheme.palette,
      // Ensure all z-indexes are above the values used in our app
      // https://mui.com/customization/z-index/
      zIndex: R.mapValues(resolvedTheme.zIndex, zIndex => zIndex * 10),
    };
  }, [effectiveTheme, uiSettings]);

  useEffect(() => {
    if (isRoot) {
      const themePalette = themeConfig.palette;
      (
        [
          // keep until we remove legacy variables.sass
          ['--color-primary', themePalette.primary.main],
          ['--color-primary-dark', themePalette.primary.dark],
          ['--color-primary-light', themePalette.primary.light],

          ['--color-success', themePalette.success.main],

          ['--color-error', themePalette.error.main],
          ['--color-error-dark', themePalette.error.dark],
          ['--color-error-light', themePalette.error.light],

          ['--color-warning', themePalette.warning.main],

          ['--text-primary', themePalette.text.primary],

          // YOLO: original brown-gray values were `themeConfig.palette.mode === 'dark' ? '#979797' : '#757575'`
          ['--text-secondary', themePalette.text.secondary],

          ['--bg-paper', themePalette.background.paper],

          ['--divider', themePalette.divider],

          // for the sake of simplicity we stick to original values, too many occurences to check
          [
            '--charcoal-gray',
            themePalette.mode === 'dark'
              ? 'rgb(44, 43, 51)'
              : 'rgb(255, 255, 255)',
          ],
          [
            '--charcoal-gray-light',
            themePalette.mode === 'dark' ? '#403e46' : '#979797',
          ],
        ] as const
      ).forEach(([variable, value]) => {
        document.documentElement.style.setProperty(variable, value);
      });
    }
  }, [isRoot, themeConfig.palette]);

  return (
    <ThemeProvider theme={themeConfig}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
